import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react";
import { brown, styled } from "../stitches.config";
import { AnyBlock, ImageSearchBlock as ImageSearchBlockType } from "../types";

import axios from "axios";
import { Button } from "../components/Button";
import { BlockInput, BrowserContainer, WEB_UI_HEIGHT } from "./WebBlock";
import { pixelToGridSize } from "../utils/gridPosition";
import Icon from "../components/Icon";
import { blockYouJustCreatedAtom } from "../jotai/atoms";
import { useUpdateAtom } from "jotai/utils";
import { DEFAULT_GRID_RESOLUTION } from "../consts";

let subscriptionKey = "3fee8fb46aec4af08d35217eeead29cb";
let host = "https://api.bing.microsoft.com";
let path = "/v7.0/images/search";

const maxResults = 25; // too long of data can overwhelm roomservice;
function makeURL(term: string, offset: number, type: string) {
  return (
    // eslint-disable-next-line no-useless-concat
    host +
    path +
    "?q=" +
    encodeURIComponent(term) +
    `&count=${maxResults}` +
    `&offset=${offset}` +
    (type ? `&imageType=${type}` : "") +
    "&maxFileSize=1500000" //1.5MB
  );
}

function ImageSearchBlock({
  block,
  updateBlock,
  justCreated,
}: {
  block: ImageSearchBlockType;
  justCreated: boolean;

  updateBlock: (block: Partial<AnyBlock>) => void;
}) {
  let [isFetching, setIsFetching] = useState(false);
  const textInputRef = useRef<HTMLInputElement>(null);
  let text = block.text;
  let data: any[] = useMemo(() => [], []);
  try {
    data = JSON.parse(block.value || "[]") as any[];
  } catch (e) {
    console.error("bad search result string");
  }
  let offset = block.offset || 0;

  const minBlockSize = 4;
  let grid_w = Math.min(Math.round(block.size.width / minBlockSize), 6);
  let grid_h = Math.min(Math.round((block.size.height - 3) / minBlockSize), 6);
  if (grid_w * grid_h > maxResults) {
    grid_w = 5;
    grid_h = 5;
  }

  const fetchData = useCallback(
    async (fresh: boolean) => {
      if (text === undefined) {
        return;
      }
      let effectiveOffset = fresh ? 0 : offset;
      setIsFetching(true);
      const result = await axios({
        method: "GET",
        url: makeURL(text, effectiveOffset, block.searchType),
        headers: {
          "Ocp-Apim-Subscription-Key": subscriptionKey,
        },
      });
      if (result.data.value.length) {
        setIsFetching(false);
        updateBlock({
          offset: effectiveOffset + grid_w * grid_h,
          value: JSON.stringify(
            result.data.value
              .slice(0, maxResults)
              .map(
                ({
                  contentUrl,
                  thumbnailUrl,
                  name,
                  width,
                  height,
                }: {
                  contentUrl: string;
                  thumbnailUrl: string;
                  name: string;
                  width: string;
                  height: string;
                }) => {
                  return { contentUrl, thumbnailUrl, name, width, height };
                }
              )
          ),
        });
      }
    },
    [text, offset, block.searchType, updateBlock, grid_w, grid_h]
  );

  useEffect(() => {
    if (!data?.length && !isFetching) {
      fetchData(true);
    }
  }, [data, isFetching, fetchData]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    e.stopPropagation(); // prevent canvas key commands from conflicting
    switch (e.key) {
      case "Escape":
        textInputRef!.current!.blur();
        break;
      case "Enter":
        fetchData(true);
        textInputRef!.current!.blur();
        break;
      default:
    }
  }

  let setBlockYouJustCreated = useUpdateAtom(blockYouJustCreatedAtom);
  useEffect(() => {
    if (justCreated && textInputRef.current) {
      textInputRef.current!.focus();
      setBlockYouJustCreated(undefined);
    }
  }, [justCreated, textInputRef, setBlockYouJustCreated]);

  let contentHeight =
    block.size.height * DEFAULT_GRID_RESOLUTION - WEB_UI_HEIGHT;
  return (
    <BrowserContainer>
      <SearchControls>
        <InputBox>
          <BlockInput
            type="text"
            value={text}
            onMouseDown={(e) => e.stopPropagation()} // prevent moving/selecting block
            onChange={(event) => {
              updateBlock({
                text: event.target.value,
              });
            }}
            ref={textInputRef}
            onKeyDown={onKeyDown}
          />
          <TypesButton
            marginOff
            title="Transparency"
            onClick={() => {
              updateBlock({
                searchType:
                  block.searchType === "transparent" ? "" : "transparent",
                value: "",
                offset: 0,
              });
            }}
          >
            <Icon
              icon="Transparent"
              color={block.searchType === "transparent" ? brown : "white"}
            />
          </TypesButton>
        </InputBox>

        <GoButton
          marginOff
          title="Search"
          onClick={() => {
            fetchData(true);
          }}
        >
          <Icon icon="Search" color={brown} />
        </GoButton>
      </SearchControls>

      <ResultsContainer
        style={{
          gridTemplateColumns: `repeat( ${grid_w}, 1fr)`,
          gridTemplateRows: `repeat(${grid_h}, 1fr)`,
        }}
      >
        {data.slice(0, grid_w * grid_h)?.map((item: any) => (
          <Img
            style={{ maxHeight: contentHeight / grid_h }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            blend={block.searchType === "transparent"}
            onDragStart={(e: React.DragEvent<HTMLImageElement>) => {
              let dTarget = e.target as HTMLImageElement;
              let { width, height } = dTarget.getBoundingClientRect();
              let aspectRatio = item.height / item.width;
              let newHeight = width * aspectRatio;
              let newWidth = height / aspectRatio;

              if (newHeight < height) {
                dTarget.style.padding = `${(height - newHeight) / 2}px 0 `;
              } else {
                dTarget.style.padding = ` 0 ${(width - newWidth) / 2}px  `;
              }
              window.setImmediate(() => {
                dTarget.style.padding = "0";
              });
              e.dataTransfer.setData(
                "text/plain",
                JSON.stringify({
                  url: item.contentUrl,
                  width: item.width,
                  height: item.height,
                })
              );
            }}
            onDoubleClick={() => {
              let aspectRatio = item.width / item.height;
              let gridSize = pixelToGridSize({
                width: item.width,
                height: item.height,
              });
              gridSize.width = Math.min(block.size.width, gridSize.width);
              gridSize.height = gridSize.width / aspectRatio;
              let blockData: Partial<AnyBlock> = {
                type: "image",
                value: item.contentUrl,
                size: gridSize,
              };
              updateBlock(blockData);
              setTimeout(() => updateBlock(blockData), 100);
            }}
            src={item.thumbnailUrl}
            key={item.contentUrl}
            alt={item.name}
          />
        ))}
      </ResultsContainer>
      <MoreButton
        icon="Plus"
        color="secondary"
        onClick={() => {
          fetchData(false);
        }}
      ></MoreButton>
    </BrowserContainer>
  );
}

export default ImageSearchBlock;
export function defaultSearch() {
  let terms = [
    "carrot",
    "campfire",
    "vines",
    "butterfly",
    "wings",
    "horse",
    "hat",
    "moss",
    "pearl",
    "snail",
    "elephant",
    "frog",
    "maple",
    "quartz",
    "cloud",
    "mushroom",
    "flower",
    "salt lamp",
    "anteater",
    "apple",
    "ant",
    "pokemon hat",
    "kawaii stickers",
    "patapon",
    "volleyball",
    "succulent",
    "airplants",
    "coffee",
  ];
  return terms[Math.floor(Math.random() * terms.length)];
}
const InputBox = styled("div", {
  position: "relative",
});
const MoreButton = styled(Button, {
  position: "absolute",
  right: 0,
  bottom: 0,
  border: "1px solid $transparentBrown40",
});

// Fixes bug with <img> object-fit not working well in grid-template
const ResultsContainer = styled("div", {
  width: "100%",
  height: "calc(100% - 48px)",
  position: "absolute",
  top: 48,
  left: 0,
  right: 0,
  bottom: 0,
  display: "grid",
  overflow: "hidden",
  backgroundColor: "$beige",
});

const Img = styled("img", {
  height: "100%",
  width: "100%",
  padding: "$1",
  objectFit: "contain",

  variants: {
    blend: {
      true: {
        mixBlendMode: "multiply",
      },
      false: {},
    },
  },
});
const GoButton = styled(Button, {
  padding: "0",
  height: "$6",
  display: "grid",
  marginLeft: ".5rem",
  marginRight: ".5rem",
  placeItems: "center",
  justifySelf: "start",
  userSelect: "none",
});

const TypesButton = styled(Button, {
  padding: "0",
  height: "$6",
  display: "grid",
  marginLeft: "2rem",
  marginRight: ".5rem",
  placeItems: "center",
  justifySelf: "start",
  userSelect: "none",
  position: "absolute",
  top: 0,
  right: 0,
});

const SearchControls = styled("div", {
  display: "grid",
  gridTemplateColumns: " 1fr  auto ",
  gridGap: "$2",
  overflow: "hidden",
  padding: "$2",
  alignItems: "center",
});
